import { useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Home, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Unified 404 page rendered by the router's wildcard route when no other
 * route matches. The wildcard wrapper checks the auth state and renders
 * this inside DashboardLayout for authenticated users (preserving the
 * sidebar/header shell) and bare for unauthenticated visitors.
 */
export function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Direct-link / external-referrer arrivals have no history to walk back
  // to — fall through to /dashboard so the button never dead-clicks.
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    void navigate({ to: '/dashboard' });
  };

  return (
    <div
      data-testid="not-found-page"
      className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <SearchX className="h-8 w-8" aria-hidden />
      </div>
      <h1 className="mb-2 text-3xl font-bold tracking-tight">404 — Page not found</h1>
      <p className="mb-1 max-w-md text-muted-foreground">
        The page you&apos;re looking for doesn&apos;t exist or has moved.
      </p>
      <p className="mb-8 max-w-md text-sm text-muted-foreground">
        Couldn&apos;t resolve{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
          {location.pathname}
        </code>
        . Check the URL or use the navigation below.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button data-testid="not-found-back" variant="outline" onClick={goBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
        <Button
          data-testid="not-found-dashboard"
          onClick={() => void navigate({ to: '/dashboard' })}
        >
          <Home className="mr-2 h-4 w-4" />
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}
